//src/common/components/generalinfosearch
import { lookupSensorFallback } from "./generalinfo.api.js";
import { fetchTyre } from "../../tyres/views/tyres.api.js";
import { fetchRepeaters, fetchSignalBoosters } from "../../unit-sensors/views/unitdiagrams/repeaters/repeaters.api.js";
import { getUnits } from "../../unit-sensors/views/handleunitdata.js";
import { openGeneralInfoForm } from "./generalinfoview.js";

// In generalinfosearch.js
let inputTimeout = null;

const ID_FIELDS = [
  'Id',
  'sensorId',
  'repeaterId',
  'imei',
  'IMEI',
  'imeiNumber',
  'sinkId',
  'externalTyreId',
  'tyreSerialNumber',
  'tyreId'
];

const TYRE_ID_FIELDS = [
  'externalTyreId',
  'tyreSerialNumber'
];

const findSensorsById = (searchText, index) => {
  const key = searchText.trim().toLowerCase();
  const matches = index.get(key) || [];

  // NOTE: Prioritising gateway/sink over repeater for now.
  // This is brittle — may need refactor if more types get added.
  const gatewayMatches = matches.filter(
    m => m.type === 'gateway' || m.type === 'sink'
  );
  if (gatewayMatches.length > 0) {
    return gatewayMatches;
  }

  // If any of the matches are repeaters, return only those:
  const repeaterMatches = matches.filter(
    m => m.type === 'repeater' || m.type === 'repeater-standalone' || m.type === 'signal-booster'
  );
  if (repeaterMatches.length > 0) {
    return repeaterMatches;
  }

  // Otherwise return everything
  return matches;
};

const buildSensorIndex = async (units) => {

  const index = new Map();
  const addIndexValue = (type, unit, item, value) => {
    if (value === null || value === undefined || value === '') return;

    const key = String(value).trim().toLowerCase();

    if (!key) return;

    if (!index.has(key)) index.set(key, []);

    index.get(key).push({
      type,
      unit,
      item
    });
  };

  const addToIndex = (type, unit, item) => {
    if (!item) return;

    for (const field of ID_FIELDS) {
      addIndexValue(type, unit, item, item[field]);
    }

    for (const field of TYRE_ID_FIELDS) {
      addIndexValue(type, unit, item, item?.tyre?.[field]);
    }

    if (!item.externalTyreId && item?.tyre?.externalTyreId) {
      item.externalTyreId = item.tyre.externalTyreId;
    }
  };

  const unitGateways = new Map(); // key: imei, value: unit
  const unitSinks = new Map();    // key: sinkId, value: unit

  const compareUnitNumbers = (a, b) => {
    if (a == null) return 1; // treat undefined as larger
    if (b == null) return -1;
    return a - b;
  };

  units.forEach(unit => {
    // top‑level gateway & sink
    // Gateway
    if (unit.imei) {
      const key = unit.imei;
      const currentUnit = unitGateways.get(key);
      if (!currentUnit || compareUnitNumbers(unit.unitNumber, currentUnit.unitNumber) < 0) {
        unitGateways.set(key, unit);
      }
    }

    // Sink
    if (unit.sinkId) {
      const key = unit.sinkId;
      const currentUnit = unitSinks.get(key);
      if (!currentUnit || compareUnitNumbers(unit.unitNumber, currentUnit.unitNumber) < 0) {
        unitSinks.set(key, unit);
      }
    }
  });

  unitGateways.forEach(unit => {
    addToIndex('gateway', unit, { imei: unit.imei });
  });

  unitSinks.forEach(unit => {
    addToIndex('sink', unit, { sinkId: unit.sinkId });
  });

  // if (unit.imei  && unit.unitNumber === 0) addToIndex('gateway', unit, { imei: unit.imei });
  // if (unit.sinkId && unit.unitNumber === 0) addToIndex('sink', unit, { sinkId: unit.sinkId });

  units.forEach(unit => {

    // wheels/spares/unpaired
    (unit.wheelSensors || []).forEach(item => addToIndex('wheel', unit, item));
    (unit.spareSensors || []).forEach(item => addToIndex('spare', unit, item));
    (unit.unpairedSensors || []).forEach(item => addToIndex('unpaired', unit, item));

    // trailers (each with its own wheels/spares)
    (unit.trailers || []).forEach(trailer => {
      (trailer.wheelSensors || []).forEach(item => addToIndex('trailer-wheel', unit, item));
      (trailer.spareSensors || []).forEach(item => addToIndex('trailer-spare', unit, item));
    });

    // standalone trailer sensors
    //(unit.trailerSensors || []).forEach(item => addToIndex('trailer-sensor', unit, item));

    // repeaters linked to this unit    
    (unit.repeaters || []).forEach(item => addToIndex('repeater', unit, item));

  });

  // Add truly standalone repeaters
  const repeaters = await fetchRepeaters();
  repeaters.forEach(item => addToIndex('repeater-standalone', null, item));

  const signalBoosters = await fetchSignalBoosters();
  signalBoosters.forEach(item => addToIndex('signal-booster', null, item));

  return index;
};

const normaliseTyreResponse = (payload) => {
  if (!payload) return null;

  if (payload.tyre) return payload.tyre;
  if (payload.result) return payload.result;

  if (payload.data) {
    return normaliseTyreResponse(payload.data);
  }

  return payload;
};

const normaliseValue = (value) => String(value ?? '').trim();

const findUnitForTyre = (tyre, units) => {
  const tyreUnitId = normaliseValue(tyre?.unitId);
  const tyreImei = normaliseValue(tyre?.imeiNumber || tyre?.imei);

  if (!tyreUnitId && !tyreImei) return null;

  const directUnit = units.find(unit =>
    normaliseValue(unit?.unitId) === tyreUnitId ||
    normaliseValue(unit?.Id) === tyreUnitId ||
    normaliseValue(unit?.imei) === tyreImei ||
    normaliseValue(unit?.imeiNumber) === tyreImei
  );

  if (directUnit) return directUnit;

  for (const parentUnit of units) {
    const linkedTrailer = (parentUnit?.trailers || []).find(trailer =>
      normaliseValue(trailer?.unitId) === tyreUnitId ||
      normaliseValue(trailer?.Id) === tyreUnitId ||
      normaliseValue(trailer?.imei) === tyreImei ||
      normaliseValue(trailer?.imeiNumber) === tyreImei
    );

    if (linkedTrailer) {
      // Existing search indexing passes parent unit for linked trailer wheels,
      // so return the parent unit here as well.
      return parentUnit;
    }
  }

  return null;
};

const resolveTypeFromTyre = (tyre) => {
  const wheelId = String(tyre?.currentWheelId || '');
  const axle = wheelId.slice(1, 3);

  if (axle === '17') return 'spare';

  return 'wheel';
};

const mergeTyreIntoWheelItem = (item, tyre) => {
  return {
    ...(item || {}),

    Id: item?.Id ?? tyre?.currentSensorId ?? null,
    id: item?.id ?? tyre?.currentSensorId ?? null,
    sensorId: item?.sensorId ?? tyre?.currentSensorId ?? null,

    wheelId: item?.wheelId ?? tyre?.currentWheelId ?? null,
    externalTyreId: tyre?.externalTyreId,

    tyre: {
      ...(item?.tyre || {}),
      ...(tyre || {})
    }
  };
};

const openGeneralInfoFromTyreId = async (externalTyreId, sensorIndex, units) => {
  const tyreResponse = await fetchTyre(externalTyreId);

  if (!tyreResponse?.success) {
    return {
      opened: false,
      found: false,
      message: tyreResponse?.message || 'No tyre found.'
    };
  }

  const tyre = normaliseTyreResponse(tyreResponse.data);

  if (!tyre?.externalTyreId) {
    return {
      opened: false,
      found: false,
      message: 'No tyre found.'
    };
  }

  if (!tyre.currentSensorId) {
    return {
      opened: false,
      found: true,
      message: `Tyre ${tyre.externalTyreId} was found, but it is not currently associated with a sensor.`
    };
  }

  const sensorId = String(tyre.currentSensorId);
  const localMatches = findSensorsById(sensorId, sensorIndex);

  if (localMatches.length) {
    const { type, unit, item } = localMatches[0];

    await openGeneralInfoForm(
      type || resolveTypeFromTyre(tyre),
      unit || findUnitForTyre(tyre, units),
      mergeTyreIntoWheelItem(item, tyre)
    );

    return {
      opened: true,
      found: true
    };
  }

  const fallbackResult = await lookupSensorFallback(sensorId);

  if (fallbackResult?.item) {
    await openGeneralInfoForm(
      fallbackResult.type || resolveTypeFromTyre(tyre),
      fallbackResult.unit || findUnitForTyre(tyre, units),
      mergeTyreIntoWheelItem(fallbackResult.item, tyre)
    );

    return {
      opened: true,
      found: true
    };
  }

  return {
    opened: false,
    found: true,
    message: `Tyre ${tyre.externalTyreId} was found, but sensor ${sensorId} could not be loaded.`
  };
};

export const initialiseSensorSearch = async (customerId) => {

  let messageShown = false;
  let lastSearchToken = null;

  // Build the index once
  const units = await getUnits();
  const sensorIndex = await buildSensorIndex(units, customerId);

  // Grab the datalist
  const dataList = document.getElementById('sensor-ids');
  dataList.innerText = ''; // clear any old options

  // Populate with every unique key (ID)
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
  });

  const sortedKeys = [...sensorIndex.keys()]
    .sort((a, b) => collator.compare(a, b));

  for (const key of sortedKeys) {
    const opt = document.createElement('option');
    opt.value = key;
    dataList.appendChild(opt);
  }

  const input = document.getElementById('sensor-search');

  input.addEventListener('input', () => {
    messageShown = false;

    if (inputTimeout) {
      clearTimeout(inputTimeout);
      inputTimeout = null;
    }
  });

  input.addEventListener('keydown', async e => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      const query = input.value.trim();
      const currentToken = Date.now();
      lastSearchToken = currentToken;

      const results = findSensorsById(query, sensorIndex);

      if (results.length) {
        let { type, unit, item } = results[0];

        // Try to enrich local result using fallback
        const fallbackResult = await lookupSensorFallback(query);
        if (lastSearchToken !== currentToken) return;
        if (fallbackResult) {
          // Merge missing data (you can be more selective if needed)
          type = type || fallbackResult.type;
          unit = unit || fallbackResult.unit;
          item = { ...fallbackResult.item, ...item }; // item fields override fallback
        }

        if (type && item) {
          input.value = '';
          openGeneralInfoForm(type, unit, item);
        } else if (lastSearchToken === currentToken) {
          if (!messageShown) {
            messageShown = true;
            alert(`No sensor data found for ID: ${query}`);
          }
        }
      } else {
        // Fallback 1: normal sensor/gateway/repeater lookup
        const fallbackResult = await lookupSensorFallback(query);

        if (lastSearchToken !== currentToken) return;

        if (fallbackResult) {
          const { type, unit, item } = fallbackResult;
          input.value = '';
          await openGeneralInfoForm(type, unit, item);
          return;
        }

        // Fallback 2: tyre ID lookup
        const tyreResult = await openGeneralInfoFromTyreId(query, sensorIndex, units);

        if (lastSearchToken !== currentToken) return;

        if (tyreResult.opened) {
          input.value = '';
          return;
        }

        if (!messageShown) {
          messageShown = true;
          alert(
            tyreResult.found
              ? tyreResult.message
              : `No sensor or tyre data found for ID: ${query}`
          );
        }
      }
    }
  });
};